import { Suspense } from 'react';
import { Link, Navigate, Outlet, useParams } from 'react-router';
import { ErrorNotice, LoadingNotice } from '../components/ErrorNotice';
import { RealtimeProvider } from '../features/realtime/RealtimeProvider';
import { MembershipContext } from '../features/workspace/membership-context';
import { useMemberships } from '../features/workspace/workspace.api';
import { phrase } from '../lib/i18n/runtime';

/** Resolves `/w/:workspaceId` against the user's memberships and provides it to child pages. */
export function WorkspaceLayout() {
  const { workspaceId } = useParams();
  const memberships = useMemberships();

  if (memberships.isPending) {
    return <LoadingNotice>{phrase('Loading workspace…')}</LoadingNotice>;
  }
  if (memberships.isError) {
    return (
      <ErrorNotice error={memberships.error} title={phrase('Workspaces could not be loaded')} />
    );
  }
  const membership = memberships.data.items.find((item) => item.workspace.id === workspaceId);
  if (membership === undefined) {
    return (
      <section className="stack">
        <h1>{phrase('Workspace not available')}</h1>
        <p>{phrase('This workspace does not exist or you are no longer a member of it.')}</p>
        <Link to="/">{phrase('Go to your workspaces')}</Link>
      </section>
    );
  }

  return (
    <MembershipContext value={membership}>
      {membership.mfaEnrollmentRequired && (
        <p className="notice notice--warn banner" role="status">
          <span aria-hidden="true">! </span>
          {membership.workspace.name} requires two-step verification for your role.{' '}
          <Link to="/security?mfa=required">{phrase('Set it up now')}</Link>
        </p>
      )}
      <RealtimeProvider workspaceId={membership.workspace.id}>
        <Suspense fallback={<LoadingNotice>{phrase('Loading page…')}</LoadingNotice>}>
          <Outlet />
        </Suspense>
      </RealtimeProvider>
    </MembershipContext>
  );
}

/** `/` → the first active workspace, or an empty state when the user has none. */
export function HomeRedirect() {
  const memberships = useMemberships();
  if (memberships.isPending) {
    return <LoadingNotice>{phrase('Loading your workspaces…')}</LoadingNotice>;
  }
  if (memberships.isError) {
    return (
      <ErrorNotice error={memberships.error} title={phrase('Workspaces could not be loaded')} />
    );
  }
  const items = memberships.data.items;
  const first = items.find((item) => item.member.status === 'ACTIVE') ?? items[0];
  if (first === undefined) {
    return (
      <section className="stack">
        <h1>{phrase('No workspace yet')}</h1>
        <p>
          {phrase(
            'You are not a member of any workspace. Ask a workspace owner to send you an invitation, then open the link from your email.',
          )}
        </p>
      </section>
    );
  }
  return <Navigate to={`/w/${first.workspace.id}`} replace />;
}
